export const PRICING_PLAN_TIERS = ["free", "pro", "team", "enterprise"];
export const PRICING_CAPABILITY_MATRIX = [
    {
        id: "best_for",
        label: "Best For",
        category: "commercial",
        coverageStatus: "complete",
        planValues: {
            free: "Individuals, learning, prototypes",
            pro: "Professional developers",
            team: "Startups and internal AI teams",
            enterprise: "Large enterprises and regulated orgs",
        },
        implementation: {
            metadataKeys: ["metadata.buyerSegment"],
            routePaths: ["/pricing"],
        },
        missingWork: [],
    },
    {
        id: "price",
        label: "Price",
        category: "commercial",
        coverageStatus: "complete",
        planValues: {
            free: "$0",
            pro: "$99/month",
            team: "$299/workspace/month",
            enterprise: "Custom",
        },
        implementation: {
            metadataKeys: ["metadata.priceMonthlyCents", "metadata.pricingLabel", "metadata.billingMode"],
            routePaths: ["/pricing", "/settings"],
            servicePaths: ["packages/gateway/src/services/billing-service.ts"],
        },
        missingWork: [],
        notes: "The default catalog now includes Free, Pro, Team, and Enterprise with Pro at $99/month and Team at $299/workspace/month.",
    },
    {
        id: "ai_agents",
        label: "AI Agents",
        category: "core",
        coverageStatus: "complete",
        planValues: {
            free: "5 agents",
            pro: "25 agents",
            team: "100 agents",
            enterprise: "Unlimited",
        },
        implementation: {
            featureKeys: ["agents.spawn"],
            quotaKeys: ["agents.max"],
            routePaths: ["/agents", "/builder", "/quick-agent"],
            apiPathHints: ["/api/v1/agents"],
            servicePaths: ["packages/gateway/src/gateway-factory.ts", "packages/gateway/src/services/workspace-metrics-service.ts"],
        },
        missingWork: [],
    },
    {
        id: "workflow_executions",
        label: "Workflow Executions",
        category: "core",
        coverageStatus: "complete",
        planValues: {
            free: "500/month",
            pro: "20,000/month",
            team: "100,000/month",
            enterprise: "Custom scale",
        },
        implementation: {
            featureKeys: ["tasks.submit"],
            quotaKeys: ["tasks.monthly"],
            routePaths: ["/tasks", "/builder", "/chat"],
            apiPathHints: ["/api/v1/tasks"],
            servicePaths: ["packages/gateway/src/gateway-factory.ts", "packages/gateway/src/services/billing-service.ts"],
        },
        missingWork: [],
    },
    {
        id: "local_runner_service",
        label: "Local Runner Service",
        category: "core",
        coverageStatus: "complete",
        planValues: {
            free: "Not included",
            pro: "2 runners",
            team: "25 runners",
            enterprise: "Unlimited",
        },
        implementation: {
            featureKeys: ["local.runners.manage"],
            quotaKeys: ["local.runners.max"],
            routePaths: ["/settings"],
            apiPathHints: ["/api/v1/local-runners"],
            servicePaths: ["packages/gateway/src/gateway-factory.ts", "packages/gateway/src/services/local-runner-service.ts"],
        },
        missingWork: [],
        notes: "Tenant administrators can register, retire, assign, and submit durable local runner tasks only when the plan enables local runner management and has remaining runner quota.",
    },
    {
        id: "visual_workflow_builder",
        label: "Visual Workflow Builder",
        category: "core",
        coverageStatus: "complete",
        planValues: {
            free: "Included",
            pro: "Included",
            team: "Included",
            enterprise: "Included",
        },
        implementation: {
            featureKeys: [
                "agents.spawn",
                "models.premium",
                "connectors.manage",
                "automation.triggers.manage",
                "threads.fork",
                "approvals.manage",
                "execution.sandbox",
                "execution.command",
                "execution.powershell",
                "execution.filesystem",
            ],
            routePaths: ["/builder"],
            apiPathHints: [
                "/api/v1/agents",
                "/api/v1/builder/workflows",
                "/api/v1/builder/workflows/:workflowId/versions",
                "/api/v1/builder/subflows",
            ],
            servicePaths: [
                "packages/gateway/src/services/builder-workflow-service.ts",
                "packages/gateway/src/services/builder-resources-service.ts",
                "packages/gateway/src/gateway-factory.ts",
            ],
        },
        missingWork: [],
        notes: "Builder access is included for every tier. Saving workflows, workflow versions, and subflows remains server-gated when graph nodes require advanced capabilities such as premium models, connectors, approval nodes, replay/fork, or privileged execution.",
    },
    {
        id: "sdk_cli_access",
        label: "SDK + CLI Access",
        category: "developer",
        coverageStatus: "complete",
        planValues: {
            free: "Included",
            pro: "Included",
            team: "Included",
            enterprise: "Included",
        },
        implementation: {
            apiPathHints: ["/api/v1/*"],
            packagePaths: ["packages/sdk", "packages/cli"],
            docPaths: ["docs/INTEGRATION_GUIDES_SDK.md", "packages/sdk/ENTITLEMENTS.md"],
        },
        missingWork: [],
        notes: "SDK and CLI packages are available to every tier. Commands share the same backend-authenticated APIs, so feature and quota boundaries are enforced by tenant entitlements instead of separate client libraries.",
    },
    {
        id: "marketplace_access",
        label: "Marketplace Access",
        category: "marketplace",
        coverageStatus: "complete",
        planValues: {
            free: "Community packs",
            pro: "Full marketplace",
            team: "Private + shared marketplace",
            enterprise: "Enterprise private marketplace",
        },
        implementation: {
            featureKeys: [
                "tenant.packs.install",
                "tenant.packs.policy",
                "marketplace.community",
                "marketplace.full",
                "marketplace.private_shared",
                "marketplace.enterprise_private",
                "marketplace.publisher.apply",
                "marketplace.publish",
            ],
            routePaths: ["/marketplace", "/marketplace/registry"],
            apiPathHints: ["/api/v1/marketplace", "/api/v1/tenant/packs/:packId/install", "/api/v1/tenant/pack-install-policy"],
            servicePaths: [
                "packages/gateway/src/services/marketplace-registry-service.ts",
                "packages/gateway/src/services/curated-marketplace-pack-service.ts",
                "packages/gateway/src/gateway-factory.ts",
            ],
            metadataKeys: ["metadata.marketplaceTier"],
        },
        missingWork: [],
        notes: "Marketplace install now separates community, full, private/shared, and enterprise-private tiers with explicit feature flags and marketplaceTier metadata. Pack install checks both the pack quality tier and supportedPlans before dispatching.",
    },
    {
        id: "model_providers",
        label: "Model Providers",
        category: "developer",
        coverageStatus: "complete",
        planValues: {
            free: "OpenAI only",
            pro: "Multi-provider",
            team: "Multi-provider",
            enterprise: "Custom/private providers",
        },
        implementation: {
            featureKeys: ["models.premium", "secrets.byo_keys"],
            routePaths: ["/settings"],
            apiPathHints: ["/api/v1/settings/model-providers", "/api/v1/settings/secrets"],
            servicePaths: ["packages/gateway/src/services/model-provider-service.ts", "packages/gateway/src/services/tenant-settings-service.ts"],
        },
        missingWork: [],
        notes: "Free workspaces are restricted to OpenAI standard models. Paid workspaces with models.premium can see multiple providers, and secrets.byo_keys controls tenant BYOK/private provider setup.",
    },
    {
        id: "observability_traces",
        label: "Observability & Traces",
        category: "observability",
        coverageStatus: "complete",
        planValues: {
            free: "Basic",
            pro: "Standard",
            team: "Advanced",
            enterprise: "Enterprise-grade",
        },
        implementation: {
            featureKeys: ["observability.read", "observability.basic", "observability.standard", "observability.advanced", "observability.enterprise"],
            routePaths: ["/observability"],
            apiPathHints: ["/api/v1/observability"],
            servicePaths: ["packages/gateway/src/services/observability-service.ts"],
            metadataKeys: ["metadata.observabilityTier", "metadata.traceRetentionDays"],
            docPaths: ["docs/GATEWAY_OBSERVABILITY_CONTRACT.md"],
        },
        missingWork: [],
        notes: "Free/Pro/Team/Enterprise observability tiers are modeled as basic trace reads, standard ingest and analytics, advanced saved-filter/governance workflows, and enterprise-grade metadata. The UI avoids advanced API calls unless observability.advanced is enabled.",
    },
    {
        id: "execution_replay",
        label: "Execution Replay",
        category: "observability",
        coverageStatus: "complete",
        planValues: {
            free: "Not included",
            pro: "Included",
            team: "Included",
            enterprise: "Included",
        },
        implementation: {
            featureKeys: ["threads.fork"],
            routePaths: ["/threads"],
            apiPathHints: ["/api/v1/threads"],
            servicePaths: ["packages/gateway/src/gateway-factory.ts"],
            docPaths: ["docs/GATEWAY_THREADS_CONTRACT.md"],
        },
        missingWork: [],
        notes: "Execution replay is enforced through the existing threads.fork entitlement for all checkpoint replay, resume, fork, cancel, import, and export actions.",
    },
    {
        id: "semantic_memory",
        label: "Semantic Memory",
        category: "core",
        coverageStatus: "complete",
        planValues: {
            free: "Not included",
            pro: "Included",
            team: "Included",
            enterprise: "Included",
        },
        implementation: {
            featureKeys: ["sessions.memory"],
            routePaths: ["/sessions"],
            apiPathHints: ["/api/v1/sessions"],
            servicePaths: ["packages/gateway/src/gateway-factory.ts"],
            docPaths: ["docs/GATEWAY_SESSION_MEMORY_CONTRACT.md"],
        },
        missingWork: [],
        notes: "Semantic memory is server-gated by sessions.memory. Free workspaces are disabled and the Sessions page suppresses memory API calls until the entitlement snapshot allows them.",
    },
    {
        id: "evaluation_studio",
        label: "Evaluation Studio",
        category: "observability",
        coverageStatus: "complete",
        planValues: {
            free: "Limited",
            pro: "Included",
            team: "Included",
            enterprise: "Advanced/custom",
        },
        implementation: {
            featureKeys: ["evaluations.manage"],
            quotaKeys: ["evaluations.datasets.max", "evaluations.experiments.max", "evaluations.runs.monthly"],
            routePaths: ["/evaluations"],
            apiPathHints: ["/api/v1/evals"],
            servicePaths: ["packages/gateway/src/services/evaluation-service.ts"],
            docPaths: ["docs/GATEWAY_EVALUATION_CONTRACT.md"],
        },
        missingWork: [],
        notes: "Free uses limited evaluation quotas; Pro, Team, and Enterprise scale through server-authoritative dataset, experiment, and monthly-run quotas.",
    },
    {
        id: "human_approval_nodes",
        label: "Human Approval Nodes",
        category: "governance",
        coverageStatus: "complete",
        planValues: {
            free: "Limited",
            pro: "Included",
            team: "Advanced approvals",
            enterprise: "Enterprise governance",
        },
        implementation: {
            featureKeys: ["approvals.manage", "approvals.advanced"],
            quotaKeys: ["approvals.monthly", "approvals.chain_templates.max"],
            routePaths: ["/approvals", "/builder"],
            apiPathHints: ["/api/v1/approvals", "/api/v1/settings/approvals"],
            servicePaths: ["packages/gateway/src/services/approval-service.ts", "packages/gateway/src/gateway-factory.ts"],
            docPaths: ["docs/GATEWAY_APPROVALS_CONTRACT.md"],
        },
        missingWork: [],
        notes: "Free and Pro can use approval nodes through approvals.manage, with monthly approval quotas. Team and Enterprise add approvals.advanced for notification automation, analytics evidence, and reusable chain templates.",
    },
    {
        id: "connector_governance",
        label: "Connector Governance",
        category: "governance",
        coverageStatus: "complete",
        planValues: {
            free: "Basic",
            pro: "Managed connectors",
            team: "Advanced governance",
            enterprise: "Full policy enforcement",
        },
        implementation: {
            featureKeys: ["connectors.catalog.read", "connectors.manage", "connectors.policy.manage", "connectors.plugins.manage", "connectors.execute.process"],
            quotaKeys: ["connectors.plugins.max"],
            routePaths: ["/connectors", "/connectors/build", "/outbound-mcp"],
            apiPathHints: ["/api/v1/connectors", "/api/v1/settings/connectors/policy"],
            servicePaths: ["packages/gateway/src/services/connector-service.ts", "packages/gateway/src/services/connector-plugin-registry-service.ts"],
            docPaths: ["docs/GATEWAY_CONNECTORS_CONTRACT.md"],
        },
        missingWork: [],
        notes: "Catalog/read posture is available through connectors.catalog.read. Managed connection mutations require connectors.manage, Team+ policy authoring uses connectors.policy.manage, custom plugin quotas use connectors.plugins.max, and Enterprise process-backed connector execution remains connectors.execute.process with shared-cache process rate and connection-lease guards.",
    },
    {
        id: "rbac",
        label: "RBAC (Role Access)",
        category: "security",
        coverageStatus: "complete",
        planValues: {
            free: "Not included",
            pro: "Basic",
            team: "Advanced",
            enterprise: "Enterprise RBAC",
        },
        implementation: {
            featureKeys: ["rbac.basic", "rbac.advanced", "rbac.enterprise"],
            routePaths: ["/settings", "/platform/users"],
            apiPathHints: ["/api/v1/tenant/invites", "/api/v1/platform/users"],
            servicePaths: ["packages/gateway/src/auth/auth-service.ts", "packages/gateway/src/services/saas-service.ts", "packages/gateway/src/gateway-factory.ts"],
            metadataKeys: ["metadata.rbacTier"],
        },
        missingWork: [],
        notes: "Tenant membership mutations require rbac.basic, non-basic role assignments are reserved for rbac.advanced, and rbacTier metadata lets SuperAdmin define custom catalog rows without plan-name checks.",
    },
    {
        id: "audit_logs",
        label: "Audit Logs",
        category: "security",
        coverageStatus: "complete",
        planValues: {
            free: "7 days",
            pro: "30 days",
            team: "90 days",
            enterprise: "Custom retention",
        },
        implementation: {
            featureKeys: ["audit.export"],
            routePaths: ["/settings", "/pricing"],
            apiPathHints: ["/api/v1/settings/audit", "/api/v1/platform/audit"],
            servicePaths: [
                "packages/gateway/src/services/audit-export-service.ts",
                "packages/gateway/src/services/audit-delivery-service.ts",
                "packages/gateway/src/services/governance-posture-service.ts",
            ],
            metadataKeys: ["metadata.auditRetentionDays"],
        },
        missingWork: [],
        notes: "Audit list/export paths now clamp queries to metadata.auditRetentionDays before reading the audit store. Enterprise/custom plans can set null for unbounded contractual retention.",
    },
    {
        id: "sso_scim",
        label: "SSO / SCIM",
        category: "security",
        coverageStatus: "complete",
        planValues: {
            free: "Not included",
            pro: "Not included",
            team: "Optional add-on",
            enterprise: "Included",
        },
        implementation: {
            featureKeys: ["auth.sso", "auth.scim"],
            routePaths: ["/settings"],
            apiPathHints: ["/api/v1/enterprise/sso", "/api/v1/enterprise/scim"],
            servicePaths: ["packages/gateway/src/services/enterprise-identity-service.ts"],
        },
        missingWork: [],
        notes: "Team add-on behavior is represented by the auth.sso/auth.scim feature flags staying off by default while SuperAdmin entitlement overrides can enable them for a workspace.",
    },
    {
        id: "api_rate_limits",
        label: "API Rate Limits",
        category: "operations",
        coverageStatus: "complete",
        planValues: {
            free: "Shared",
            pro: "Higher",
            team: "Dedicated tier",
            enterprise: "Custom",
        },
        implementation: {
            quotaKeys: ["api.requests.minute", "connectors.actions.minute"],
            servicePaths: [
                "packages/gateway/src/rate-limiter.ts",
                "packages/gateway/src/interceptors/trust.ts",
                "packages/gateway/src/gateway-factory.ts",
                "packages/gateway/src/services/connector-service.ts",
            ],
            apiPathHints: ["/api/v1/tasks", "/api/v1/connectors"],
            metadataKeys: ["metadata.rateLimitTier"],
        },
        missingWork: [],
        notes: "Gateway REST traffic consumes api.requests.minute through the shared cache minute bucket, connector actions consume connectors.actions.minute through the connector service entitlement quota, and process connector invocations use shared-cache minute buckets for cross-node enforcement.",
    },
    {
        id: "concurrent_executions",
        label: "Concurrent Executions",
        category: "operations",
        coverageStatus: "complete",
        planValues: {
            free: "2",
            pro: "10",
            team: "50",
            enterprise: "Unlimited/custom",
        },
        implementation: {
            quotaKeys: ["tasks.concurrent"],
            routePaths: ["/tasks", "/operations"],
            apiPathHints: ["/api/v1/tasks"],
            servicePaths: ["packages/gateway/src/gateway-factory.ts"],
        },
        missingWork: [],
    },
    {
        id: "deployment_types",
        label: "Deployment Types",
        category: "operations",
        coverageStatus: "complete",
        planValues: {
            free: "Cloud only",
            pro: "Cloud",
            team: "Cloud + Hybrid",
            enterprise: "Self-hosted / Air-gapped",
        },
        implementation: {
            featureKeys: ["deployment.custom_domain"],
            routePaths: ["/settings", "/platform/workspaces"],
            metadataKeys: ["metadata.deploymentTypes"],
            docPaths: ["docs/DEPLOYMENT_RUNBOOK.md", "docs/REFERENCE_ARCHITECTURE_DEDICATED_DATA_PLANE.md"],
        },
        missingWork: [],
        notes: "Deployment shape is represented as structured plan metadata for pricing and SuperAdmin-created plans; physical deployment delivery remains a platform operations concern.",
    },
    {
        id: "secrets_management",
        label: "Secrets Management",
        category: "security",
        coverageStatus: "complete",
        planValues: {
            free: "Basic",
            pro: "Included",
            team: "Advanced",
            enterprise: "Enterprise vault integrations",
        },
        implementation: {
            featureKeys: ["secrets.manage", "secrets.byo_keys", "secrets.external_vault"],
            routePaths: ["/settings"],
            apiPathHints: ["/api/v1/settings/secrets"],
            servicePaths: ["packages/gateway/src/services/tenant-settings-service.ts", "packages/gateway/src/services/model-provider-service.ts"],
        },
        missingWork: [],
        notes: "All tiers retain platform-managed secret storage through secrets.manage. Team+ unlocks tenant-managed BYOK/private-provider keys with secrets.byo_keys, and Enterprise carries the secrets.external_vault entitlement for vault-backed deployments and SuperAdmin overrides.",
    },
    {
        id: "environment_promotion",
        label: "Environment Promotion",
        category: "operations",
        coverageStatus: "complete",
        planValues: {
            free: "Not included",
            pro: "Dev/Prod",
            team: "Dev/Staging/Prod",
            enterprise: "Multi-environment governance",
        },
        implementation: {
            featureKeys: ["agent.releases.manage", "agent.releases.staging", "rollouts.manage", "workspace.reviews.manage"],
            routePaths: ["/agent-releases"],
            apiPathHints: ["/api/v1/agent-releases", "/api/v1/agents/{agentId}/rollout"],
            servicePaths: ["packages/gateway/src/services/agent-release-service.ts", "packages/gateway/src/services/rollout-service.ts"],
            docPaths: ["docs/GATEWAY_AGENT_RELEASE_MANAGEMENT_CONTRACT.md"],
        },
        missingWork: [],
        notes: "Pro receives release and rollout management for Dev/Prod. Team and Enterprise add agent.releases.staging for Dev/Staging/Prod, while workspace review governance remains Team+ through workspace.reviews.manage.",
    },
    {
        id: "runtime_analytics",
        label: "Runtime Analytics",
        category: "observability",
        coverageStatus: "complete",
        planValues: {
            free: "Basic",
            pro: "Standard",
            team: "Advanced",
            enterprise: "Full analytics suite",
        },
        implementation: {
            featureKeys: ["analytics.basic", "analytics.standard", "analytics.advanced", "analytics.full_suite", "observability.read", "business.roi"],
            routePaths: ["/dashboard", "/observability", "/business-roi"],
            apiPathHints: ["/api/v1/observability", "/api/v1/roi"],
            servicePaths: ["packages/gateway/src/services/workspace-metrics-service.ts", "packages/gateway/src/services/roi-service.ts"],
            metadataKeys: ["metadata.analyticsTier"],
        },
        missingWork: [],
        notes: "Dashboard/basic analytics are represented by analytics.basic. Pro adds observability-backed standard analytics, Team adds advanced analytics and ROI, and Enterprise carries analytics.full_suite plus custom metadata.",
    },
    {
        id: "trace_retention",
        label: "Trace Retention",
        category: "observability",
        coverageStatus: "complete",
        planValues: {
            free: "7 days",
            pro: "30 days",
            team: "90 days",
            enterprise: "Custom",
        },
        implementation: {
            featureKeys: ["observability.read"],
            routePaths: ["/observability"],
            apiPathHints: ["/api/v1/observability"],
            servicePaths: ["packages/gateway/src/services/observability-service.ts"],
            metadataKeys: ["metadata.traceRetentionDays"],
        },
        missingWork: [],
        notes: "Observability reads, trace detail, task traces, analytics, and governance policy updates are capped by metadata.traceRetentionDays. Enterprise/custom plans can set null for contractual retention.",
    },
    {
        id: "support",
        label: "Support",
        category: "commercial",
        coverageStatus: "complete",
        planValues: {
            free: "Community",
            pro: "Priority email",
            team: "Priority + Slack",
            enterprise: "Dedicated support and onboarding",
        },
        implementation: {
            routePaths: ["/platform/workspaces"],
            apiPathHints: ["/api/v1/platform/support-sessions"],
            servicePaths: ["packages/gateway/src/services/platform-support-service.ts"],
            metadataKeys: ["metadata.supportTier"],
        },
        missingWork: [],
    },
    {
        id: "sla",
        label: "SLA",
        category: "commercial",
        coverageStatus: "complete",
        planValues: {
            free: "Not included",
            pro: "Not included",
            team: "99.9%",
            enterprise: "Custom SLA",
        },
        implementation: {
            metadataKeys: ["metadata.sla"],
            routePaths: ["/pricing", "/platform/plans"],
        },
        missingWork: [],
    },
    {
        id: "security_review_assistance",
        label: "Security Review Assistance",
        category: "commercial",
        coverageStatus: "complete",
        planValues: {
            free: "Not included",
            pro: "Not included",
            team: "Limited",
            enterprise: "Included",
        },
        implementation: {
            featureKeys: ["audit.export"],
            routePaths: ["/settings", "/agent-releases"],
            servicePaths: ["packages/gateway/src/services/agent-trust-dossier-service.ts", "packages/gateway/src/services/audit-export-service.ts"],
            metadataKeys: ["metadata.securityReviewAssistance"],
        },
        missingWork: [],
    },
    {
        id: "deployment_assistance",
        label: "Deployment Assistance",
        category: "commercial",
        coverageStatus: "complete",
        planValues: {
            free: "Not included",
            pro: "Not included",
            team: "Optional",
            enterprise: "Included",
        },
        implementation: {
            metadataKeys: ["metadata.deploymentAssistance"],
            routePaths: ["/pricing", "/platform/plans"],
        },
        missingWork: [],
    },
    {
        id: "custom_connectors",
        label: "Custom Connectors",
        category: "developer",
        coverageStatus: "complete",
        planValues: {
            free: "Not included",
            pro: "Limited",
            team: "Included",
            enterprise: "Unlimited",
        },
        implementation: {
            featureKeys: ["connectors.plugins.manage", "connectors.execute.process"],
            quotaKeys: ["connectors.plugins.max"],
            routePaths: ["/connectors/build"],
            apiPathHints: ["/api/v1/connectors/plugins"],
            servicePaths: ["packages/gateway/src/services/connector-plugin-registry-service.ts"],
            docPaths: ["docs/CONNECTOR_PLUGIN_RUNTIME_PLAN.md"],
        },
        missingWork: [],
        notes: "Connector plugin creation now honors the connectors.plugins.max quota; Enterprise remains unlimited and SuperAdmin-created plans can override the quota dynamically.",
    },
    {
        id: "private_networking",
        label: "Private Networking",
        category: "security",
        coverageStatus: "complete",
        planValues: {
            free: "Not included",
            pro: "Not included",
            team: "Optional",
            enterprise: "Included",
        },
        implementation: {
            metadataKeys: ["metadata.privateNetworking"],
            routePaths: ["/pricing", "/platform/plans"],
            docPaths: ["docs/REFERENCE_ARCHITECTURE_DEDICATED_DATA_PLANE.md"],
        },
        missingWork: [],
    },
    {
        id: "compliance_features",
        label: "Compliance Features",
        category: "security",
        coverageStatus: "complete",
        planValues: {
            free: "Basic",
            pro: "Standard",
            team: "Advanced",
            enterprise: "SOC2 / enterprise controls",
        },
        implementation: {
            featureKeys: ["audit.export", "auth.sso", "auth.scim", "secrets.byo_keys", "secrets.external_vault"],
            routePaths: ["/settings", "/admin/dlp", "/agent-releases"],
            apiPathHints: ["/api/v1/settings/audit", "/api/v1/settings/dlp", "/api/v1/enterprise"],
            servicePaths: ["packages/gateway/src/services/governance-posture-service.ts", "packages/gateway/src/services/tenant-dlp-policy-service.ts"],
            metadataKeys: ["metadata.complianceTier"],
        },
        missingWork: [],
        notes: "The commercial compliance tier is represented in plan metadata and backed by progressively enabled audit export, DLP/governance posture, SSO/SCIM, BYOK, and Enterprise vault entitlements.",
    },
    {
        id: "recommended_cta",
        label: "Recommended CTA",
        category: "commercial",
        coverageStatus: "complete",
        planValues: {
            free: "Start Free",
            pro: "Start Trial",
            team: "Contact Sales",
            enterprise: "Talk to Sales",
        },
        implementation: {
            metadataKeys: ["metadata.cta"],
            routePaths: ["/pricing", "/signup"],
        },
        missingWork: [],
    },
];
export const PRICING_CAPABILITY_FEATURE_KEYS = Array.from(new Set(PRICING_CAPABILITY_MATRIX.flatMap((entry) => [
    ...(entry.implementation.featureKeys ?? []),
    ...(entry.implementation.quotaKeys ?? []),
]))).sort();
export const PRICING_CAPABILITY_METADATA_KEYS = Array.from(new Set(PRICING_CAPABILITY_MATRIX.flatMap((entry) => entry.implementation.metadataKeys ?? []))).sort();
export function pricingCapabilitiesByStatus(status) {
    return PRICING_CAPABILITY_MATRIX.filter((entry) => entry.coverageStatus === status);
}
export function pricingCapabilityNeedsBuildout(entry) {
    return entry.coverageStatus !== "complete" || entry.missingWork.length > 0;
}
//# sourceMappingURL=plan-capability-matrix.js.map